import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './Homepage.css';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import apnagharLogo from './apnaghar.png';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import bgBackground from './bg_background.jpg';
import { FaStar, FaMapMarkerAlt, FaUserFriends, FaBed, FaCheckCircle, FaEye, FaMedal, FaEnvelope, FaInstagram, FaShieldAlt } from 'react-icons/fa';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { addDoc, onSnapshot, serverTimestamp, doc, getDoc, setDoc, collection, getDocs } from "firebase/firestore";
import { formatDistanceToNow } from "date-fns";
import { getStorage, ref, listAll, getDownloadURL } from 'firebase/storage';
import { db, auth, app } from './firebase';
import AvatarDialog from "./AvatarDialog";
import { getAuth } from "firebase/auth";
import AeroSearch from "../components/AeroSearch";
import { Helmet } from "react-helmet-async";

function Homepage() {
  const navigate = useNavigate();
  const storage = getStorage(app);
  const authInstance = getAuth();

  const [adImages, setAdImages] = useState([]);
  const [homepageListings, setHomepageListings] = useState([]);
  const [scratchDisabled, setScratchDisabled] = useState(false);
  const [revealedCard, setRevealedCard] = useState(null);
  const [scratchResult, setScratchResult] = useState('');
  const [timerMessage, setTimerMessage] = useState('');
  const [spinningCard, setSpinningCard] = useState(null);
  const [cardText, setCardText] = useState("Scratch Me");
  const [timeLeft, setTimeLeft] = useState(0);
  const [showDialog, setShowDialog] = useState(false);
  const [welcomeClaimed, setWelcomeClaimed] = useState(false);
  const API_REACT = process.env.REACT_APP_API_REACT;
  const [feedbacks, setFeedbacks] = useState([]);
  const [newFeedback, setNewFeedback] = useState("");
  const [rating, setRating] = useState(0);
  const locationCache = {};
  const searchRef = useRef(null);
  const [showFloatingIcon, setShowFloatingIcon] = useState(false);
  const [isSpinning, setIsSpinning] = useState([false, false, false]);
  const [cardTexts, setCardTexts] = useState(["Scratch Me", "Scratch Me", "Scratch Me"]);

  const handleSearch = (query) => {
    console.log("Searching for:", query);
  };

  const scrollToSearch = useCallback(() => {
    if (searchRef.current) {
      searchRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  useEffect(() => {
    if (!auth.currentUser) {
      console.log("Guest user browsing homepage...");
    }
  }, []);

  /** ---------- WELCOME BONUS POPUP ---------- **/
  useEffect(() => {
    const checkWelcomeClaim = async () => {
      const user = authInstance.currentUser;
      if (!user) {
        setShowDialog(false);
        return;
      }

      try {
        const userRef = doc(db, "user-data", user.uid);
        const snap = await getDoc(userRef);
        if (snap.exists()) {
          setWelcomeClaimed(snap.data().welcomeClaimed || false);
        }
      } catch (err) {
        console.error("Error checking welcomeClaim:", err);
      }
    };

    checkWelcomeClaim();

    if (authInstance.currentUser && !welcomeClaimed) {
      setShowDialog(true);
      const interval = setInterval(() => {
        if (authInstance.currentUser && !welcomeClaimed) {
          setShowDialog(true);
        }
      }, 30 * 60 * 1000);
      return () => clearInterval(interval);
    }

  }, [authInstance, welcomeClaimed]);

  const handleClaimBonus = useCallback(() => {
    setWelcomeClaimed(true);
    setShowDialog(false);
    navigate("offers");
  }, [navigate]);

  /** ---------- FETCH ADS ---------- **/
  useEffect(() => {
    const fetchAds = async () => {
      try {
        const adsRef = ref(storage, 'rental_ads_photo/');
        const res = await listAll(adsRef);
        const urls = await Promise.all(res.items.map(item => getDownloadURL(item)));
        setAdImages(urls);
      } catch (err) {
        console.error('Error fetching ad images:', err);
      }
    };
    fetchAds();
  }, [storage]);

  const fetchCityStateFromMapLink = async (mapLink) => {
    if (!mapLink) return "Assam";
    if (locationCache[mapLink]) return locationCache[mapLink];

    try {
      const url = new URL(mapLink);
      const coords = url.searchParams.get("q");
      if (!coords) return "Assam";
      const [lat, lng] = coords.split(",");
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      const res = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`);
      const data = await res.json();
      if (data.status === "OK" && data.results.length > 0) {
        const components = data.results[0].address_components;
        const city = components.find(c => c.types.includes("locality"))?.long_name ||
                     components.find(c => c.types.includes("administrative_area_level_2"))?.long_name ||
                     "Unknown City";
        const state = components.find(c => c.types.includes("administrative_area_level_1"))?.long_name || "Assam";
        const loc = `${city}, ${state}`;
        locationCache[mapLink] = loc;
        return loc;
      }
    } catch (err) {
      console.error(err);
    }
    return "Assam";
  };

  /** ---------- FETCH TOP LISTINGS ---------- **/
  const combineAndSet = useCallback(async (houseData, rentData) => {
    const combined = [...houseData, ...rentData];
    const homepageOnly = combined.filter(listing => listing.showOnHomepage);

    const listingsWithUrls = await Promise.all(
      homepageOnly.map(async (listing) => {
        const thumbnailPath = listing.thumbnailPath || (listing.images?.[0] && `${listing.type === "house" ? "house-listings" : "rent-listings"}/${listing.id}/${listing.images[0]}`);
        let imageUrl = null;
        if (thumbnailPath) {
          try { imageUrl = await getDownloadURL(ref(storage, thumbnailPath)); }
          catch(err) { console.error("Failed to fetch thumbnail:", listing.id, err); }
        }

        let location = listing.location;
        if (listing.mapLink) {
          location = await fetchCityStateFromMapLink(listing.mapLink);
        }

        return { ...listing, imageUrl, location };
      })
    );

    const sorted = listingsWithUrls.sort((a, b) => (b.views || 0) - (a.views || 0));
    setHomepageListings(sorted.slice(0, 20));
  }, [storage]);

  useEffect(() => {
    const houseRef = collection(db, "house-listings");
    const rentRef = collection(db, "rent-listings");

    const fetchAndCombine = async () => {
      try {
        const [houseSnap, rentSnap] = await Promise.all([getDocs(houseRef), getDocs(rentRef)]);
        const houseData = houseSnap.docs.map(d => ({ id: d.id, ...d.data(), type: "house" }));
        const rentData = rentSnap.docs.map(d => ({ id: d.id, ...d.data(), type: "rent" }));
        combineAndSet(houseData, rentData);
      } catch (err) {
        console.error("Error fetching listings:", err);
      }
    };
    fetchAndCombine();

    const unsubHouses = onSnapshot(houseRef, async (houseSnap) => {
      const houseData = houseSnap.docs.map(d => ({ id: d.id, ...d.data(), type: "house" }));
      const rentSnap = await getDocs(rentRef);
      const rentData = rentSnap.docs.map(d => ({ id: d.id, ...d.data(), type: "rent" }));
      combineAndSet(houseData, rentData);
    });

    const unsubRents = onSnapshot(rentRef, async (rentSnap) => {
      const rentData = rentSnap.docs.map(d => ({ id: d.id, ...d.data(), type: "rent" }));
      const houseSnap = await getDocs(houseRef);
      const houseData = houseSnap.docs.map(d => ({ id: d.id, ...d.data(), type: "house" }));
      combineAndSet(houseData, rentData);
    });

    return () => { unsubHouses(); unsubRents(); };
  }, [combineAndSet]);

  /** ---------- DAILY REWARD COOLDOWN ---------- **/
  useEffect(() => {
    const initCooldown = async () => {
      const user = auth.currentUser;
      if (!user) return;

      try {
        const userRef = doc(db, "user-data", user.uid);
        const snap = await getDoc(userRef);
        if (snap.exists()) {
          const lastScratch = snap.data()?.lastScratchTime || 0;
          const cooldown = 24 * 60 * 60 * 1000;
          const remaining = cooldown - (Date.now() - lastScratch);
          if (remaining > 0) {
            setTimeLeft(remaining);
            setScratchDisabled(true);
          }
        }
      } catch (err) {
        console.error("Cooldown init failed:", err);
      }
    };
    initCooldown();
  }, []);

  useEffect(() => {
    if (timeLeft <= 0) return;
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1000) { clearInterval(interval); setScratchDisabled(false); setTimerMessage(''); return 0; }
        const next = prev - 1000;
        const h = Math.floor((next / (1000 * 60 * 60)) % 24);
        const m = Math.floor((next / (1000 * 60)) % 60);
        const s = Math.floor((next / 1000) % 60);
        setTimerMessage(`Try again in ${h}h ${m}m ${s}s`);
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [timeLeft]);

  /** ---------- HANDLE SCRATCH ---------- **/
  const handleScratch = async (cardIndex) => {
    if (!auth.currentUser) {
      toast.error("Sign in to play scratch card!");
      return;
    }
    if (scratchDisabled) return;

    const newSpin = [...isSpinning];
    newSpin[cardIndex] = true;
    setIsSpinning(newSpin);

    const newTexts = [...cardTexts];
    newTexts[cardIndex] = "🎡 Spinning...";
    setCardTexts(newTexts);

    try {
      const idToken = await auth.currentUser.getIdToken(true);
      const res = await fetch(`${API_REACT}/api/scratch-card`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
      });

      const data = await res.json();

      setTimeout(() => {
        const spin = [...isSpinning];
        spin[cardIndex] = false;
        setIsSpinning(spin);

        const texts = [...cardTexts];
        texts[cardIndex] = data.success
          ? (data.won ? `🎉 Won ${data.reward} tokens!` : "😢 Try again tomorrow!")
          : "Error!";
        setCardTexts(texts);

        if (data.success) {
          toast[data.won ? "success" : "info"](data.message);
          setScratchDisabled(true);
          setTimeLeft(24 * 60 * 60 * 1000);
        }
      }, 1500);

    } catch (err) {
      console.error(err);
      toast.error("Server error");

      const spin = [...isSpinning];
      spin[cardIndex] = false;
      setIsSpinning(spin);

      const texts = [...cardTexts];
      texts[cardIndex] = "Error!";
      setCardTexts(texts);
    }
  };

  const handleGiftClick = async () => {
    if (!auth.currentUser || !revealedCard) return;

    try {
      const userRef = doc(db, "user-data", auth.currentUser.uid);
      await setDoc(userRef, { lastScratchTime: Date.now() }, { merge: true });
      toast.success("🎉 Reward claimed! Come back tomorrow!");
      setScratchDisabled(true);
      setTimeLeft(24 * 60 * 60 * 1000);
    } catch (err) {
      console.error(err);
      toast.error("Could not claim reward.");
    }
  };

  /** ---------- FEEDBACK ---------- **/
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "feedback"), (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setFeedbacks(data.sort((a,b) => (a.timestamp?.seconds || 0) < (b.timestamp?.seconds || 0) ? 1 : -1));
    });
    return () => unsub();
  }, []);

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    if (!auth.currentUser) { toast.error("Please sign in to leave feedback!"); return; }
    if (rating === 0 || newFeedback.trim() === "") { toast.info("Please give a rating and message!"); return; }

    try {
      const userRef = doc(db, "user-data", auth.currentUser.uid);
      const userSnap = await getDoc(userRef);
      const username = userSnap.exists() ? userSnap.data()?.username || "Anonymous" : "Anonymous";
      const avatarUrl = userSnap.exists() ? userSnap.data()?.avatarUrl || "" : "";

      await setDoc(doc(db, "feedback", auth.currentUser.uid), {
        feedback: newFeedback,
        stars: rating,
        userId: auth.currentUser.uid,
        username,
        avatarUrl,
        timestamp: serverTimestamp(),
      });

      setNewFeedback("");
      setRating(0);
      toast.success("Your feedback has been submitted!");
    } catch (err) {
      console.error(err);
      toast.error("Error submitting feedback!");
    }
  };

  const getListingRoute = useCallback((type) => {
    if (type === "house") return "/pg";
    if (type === "rent") return "/rent";
    return "/";
  }, []);

  const memoizedListings = useMemo(() => homepageListings, [homepageListings]);

  return (
    <>
      <Helmet>
        <title>ApkaGhr | PG, Rent & Houses in Assam - 100% Genuine Listings</title>
        <meta name="description" content="Find PGs, rental houses, and homes across Assam with zero broker fees. Trusted, verified, and 100% genuine listings." />
        <meta name="keywords" content="PG in Assam, Rent house Assam, Paying guest Assam, Assam homes, Verified PG, Assam rentals" />
        <meta name="author" content="ApkaGhr Team" />
        <meta property="og:title" content="ApkaGhr | PG, Rent & Houses in Assam" />
        <meta property="og:description" content="Find PGs, rental houses, and homes across Assam with zero broker fees. Trusted & verified." />
        <meta property="og:image" content={apnagharLogo} />
        <meta property="og:url" content="https://www.apkaghr.com/homepage" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="ApkaGhr | PG, Rent & Houses in Assam" />
        <meta name="twitter:description" content="Find PGs, rental houses, and homes across Assam with zero broker fees." />
        <meta name="twitter:image" content={apnagharLogo} />
      </Helmet>

      <motion.div className="homepage-container" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
        <ToastContainer position="top-right" autoClose={3000} hideProgressBar newestOnTop closeOnClick pauseOnHover />

        {/* HERO */}
        <div className="hero-section">
          <div className="hero-card" style={{ backgroundImage: `url(${bgBackground})` }}>
            <div className="brand-top">
              <img src={apnagharLogo} alt="Logo" className="logo-img" />
              <h1 className="brand-text">ApkaGhr</h1>
            </div>
            <div className="hero-text">
              <h2>Find Your Perfect Space</h2>
              <h1>Fast Search. Zero Fees. 100% Genuine.</h1>
              <p className="hero-subtitle">
                <span>No brokers. No commission. Just genuine homes.</span>
                <span>Because home should feel like home.</span>
                <span>PG, ভাড়া আৰু ঘৰ — For Assam, By Assam, With Love</span>
              </p>
            </div>
          </div>
        </div>

        {/* EXPLORE */}
        <div className="explore-section">
          <h2 className="explore-title">Explore Our Listings</h2>
          <h4 className="explore-subtitle">
            🏠 Discover PGs, rentals, and full houses across Assam & nearby regions — fast and hassle-free.
          </h4>
          <p className="explore-tagline">✨ List easily. Search freely. Live happily.</p>
          <div className="explore-search" ref={searchRef}>
            <AeroSearch onSearch={handleSearch} />
          </div>
          <div className="categories">
            <div className="category-card" onClick={() => { navigate("/homepage/pg-listing"); if (!auth.currentUser) toast.info("You can browse listings as a guest. Sign in for more features!"); }}>
              <img src="https://cdn-icons-png.flaticon.com/512/194/194931.png" alt="PG" />
              <h4>Paying Guest</h4>
            </div>
            <div className="category-card" onClick={() => { navigate("/homepage/rent-listing"); if (!auth.currentUser) toast.info("You can browse listings as a guest. Sign in for more features!"); }}>
              <img src="https://cdn-icons-png.flaticon.com/512/69/69524.png" alt="Rent" />
              <h4>Rent House</h4>
            </div>
          </div>
        </div>

        {/* TOP 20 LISTINGS */}
        <div className="top20-section">
          <h2 className="top20-title">Top 20 Listings</h2>
          <p className="top20-subtitle"><FaShieldAlt style={{ marginRight: "6px", color: "#4CAF50" }} />These are the most trusted PGs, verified for quality, safety, and comfort across Assam.</p>
          <p className="top20-extra">🌟 Handpicked with care — only the best stays make it to our Top 20.</p>

          {homepageListings.length > 0 ? (
            <Swiper
              key={homepageListings.length}
              modules={[Navigation, Pagination]}
              navigation
              pagination={{ clickable: true }}
              spaceBetween={20}
              slidesPerView={1.2}
              watchOverflow
              breakpoints={{
                320: { slidesPerView: 1.1, spaceBetween: 12 },
                480: { slidesPerView: 1.5, spaceBetween: 14 },
                768: { slidesPerView: 2.2, spaceBetween: 16 },
                1024: { slidesPerView: 3, spaceBetween: 18 },
                1280: { slidesPerView: 4, spaceBetween: 20 },
                1440: { slidesPerView: 5, spaceBetween: 22 },
              }}
            >
              {homepageListings.map((card) => (
                <SwiperSlide key={card.id}>
                  <div className="top20-card-wrapper top20-gold-card top20-verified" onClick={() => { if (!auth.currentUser) { toast.info("Please sign in to view these properties!"); return; } navigate(`${getListingRoute(card.type)}/${card.id}`); }}>
                    <div className="gold-badge"><FaMedal /> Featured</div>
                    <img
  src={card.imageUrl || "https://via.placeholder.com/250x150"}
  alt={card.pgName || "Listing"}
  loading="lazy" // <- this is key
/>

                    <div className="pg-content">
                      <h3>{card.pgName || card.title || "Untitled"} <FaCheckCircle /></h3>
                      <p><FaMapMarkerAlt /> {card.location || "Assam"}</p>
                      <p><FaUserFriends /> {card.occupancy || "N/A"}</p>
                      <p><FaBed /> {card.roomType || "N/A"}</p>
                      <p className="price">₹ {card.rent || card.price || "N/A"}/month</p>
                      <p className="rating">
                        {[1,2,3,4,5].map(n => <FaStar key={n} color={n <= Math.round(card.ratingAvg || 0) ? "#f5c518" : "#d0d0d0"} />)}
                        <span>{card.ratingCount ? (card.ratingAvg || 0).toFixed(1) : "No ratings"}</span>
                        {card.ratingCount > 0 && <span className="views"><FaEye /> {card.ratingCount}</span>}
                      </p>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          ) : <p>Loading listings...</p>}
        </div>

        {/* ADS + DAILY REWARD */}
        <div className="ad-slider-section">
          {adImages.length > 0 && (
            <>
              <h2 className="ad-slider-title">What's New</h2>
              <Swiper modules={[Navigation, Pagination, Autoplay]} navigation pagination autoplay={{ delay: 3000 }} speed={1000} spaceBetween={30} slidesPerView={1} loop>
               {adImages.map((url, i) => (
  <SwiperSlide key={i}>
    <img src={url} alt={`Ad ${i}`} loading="lazy" className="ad-image" />
  </SwiperSlide>
))}

              </Swiper>
            </>
          )}
          <div className="reward-section">
            <h2 className="reward-title">Daily Rewards</h2>
            <p className="reward-subtitle"><span className="reward-icon">🎁</span> Scratch & Win Amazing Rewards!</p>
            <div className="scratch-card-container">
              {cardTexts.map((text, index) => (
                <div key={index} className={`scratch-card ${scratchDisabled || !auth.currentUser ? 'disabled' : ''} ${isSpinning[index] ? 'spinning' : ''}`} onClick={() => { if (!auth.currentUser) { toast.error("Sign in to play scratch card!"); return; } if (!scratchDisabled) handleScratch(index); }}>
                  <span>{text}</span>
                </div>
              ))}
            </div>
            <button onClick={handleGiftClick} disabled={scratchDisabled}>🎁 Claim Daily Gift</button>
            {timerMessage && <p>{timerMessage}</p>}
          </div>
        </div>

        {/* FEEDBACK SECTION */}
        <div className="feedback-section">
          <h2 className="feedback-title"><span className="feedback-icon">💬</span> Feedback Section </h2>
          <p className="feedback-subtitle">What Our Users Say</p>
          <form className="feedback-form" onSubmit={handleFeedbackSubmit}>
            <div className="star-rating">
              {[1,2,3,4,5].map((star) => (<FaStar key={star} size={28} color={star <= rating ? "#ffcc00" : "#d0d0d0"} onClick={() => setRating(star)} style={{ cursor: "pointer", transition: "0.3s" }} />))}
            </div>
            <textarea value={newFeedback} onChange={(e) => setNewFeedback(e.target.value)} placeholder="Share your experience..." />
            <button type="submit">Submit Feedback</button>
          </form>

          <div className="feedback-list">
            {feedbacks.length === 0 ? (<p>No feedback yet. Be the first!</p>) : (feedbacks.map((fb) => {
              const time = fb.timestamp?.toDate ? formatDistanceToNow(fb.timestamp.toDate(), { addSuffix: true }) : "Just now";
              return (
                <div className="feedback-card" key={fb.id}>
                  <div className="feedback-header">
                    <div className="feedback-user">
                      {fb.avatarUrl ? <img src={fb.avatarUrl} alt="avatar" className="feedback-avatar" /> : <div className="feedback-avatar placeholder">👤</div>}
                      <strong>{fb.username || "Anonymous"}</strong>
                    </div>
                    <div className="feedback-stars">
                      {[1,2,3,4,5].map(n => <FaStar key={n} size={16} color={n <= fb.stars ? "#ffcc00" : "#d0d0d0"} />)}
                      <span className="rating-value">{fb.stars.toFixed(1)}</span>
                    </div>
                  </div>
                  <p className="feedback-text">{fb.feedback}</p>
                  <span className="feedback-time">{time}</span>
                </div>
              );
            }))}
          </div>
        </div>

        {/* FOOTER */}
        <footer className="app-footer">
          <div className="footer-content">
            <div className="footer-logo">
              <img src={apnagharLogo} alt="Apnaghar Logo" className="footer-logo-img" />
              <span className="footer-brand">ApkaGhr</span>
            </div>
            <div className="footer-info">
              <div className="footer-contact">
                <FaEnvelope className="footer-icon" />
                <a href="mailto:support@apkaghr.com?subject=Need%20Support&body=Hi%20Team%2C%0A%0AI%20need%20help%20with...">support@apkaghr.com</a>
              </div>
              <div className="footer-social">
                <FaInstagram className="footer-icon" />
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer">Follow us</a>
              </div>
            </div>
          </div>
        </footer>

        {!welcomeClaimed && showDialog && <AvatarDialog onClose={() => setShowDialog(false)} onClaim={handleClaimBonus} />}
      </motion.div>
    </>
  );
}

export default Homepage;
